// ============================================================
// YOM Intent endpoints (docs/intent.md §7, §8), injected into v2Api so they
// share its auth and cache: status, segments with counts, exports with their
// last run, and starting a run.
// ============================================================

import { v2Api } from './v2Api'
import { unwrap, type V2Envelope } from './v2Mappers'

export type IntentStage = 'browsing' | 'considering' | 'intent' | 'purchased'

export interface IntentStatus {
    enabled: boolean
    deployment: boolean
    project: boolean
    connectors: string[]
    /** Per connector, what a member should know before using it (Google Customer Match eligibility). */
    connector_notes?: Record<string, string>
    taxonomy: { version: string; param: string }
    retention_days: number
    members: Array<{ sub_tenant: string; profiles: number; last_seen: string | null }>
}

export interface IntentSegmentCount {
    profiles: number
    by_stage: Record<IntentStage, number>
    advertising: number
    advertising_explicit: number
}

export interface IntentSegment {
    id: string
    name: string
    sub_tenant: string
    definition: {
        categories: number[]
        stages: IntentStage[]
        minScore: number
        recencyDays: number
        excludePurchased: boolean
        consent: 'any' | 'advertising'
    }
    categories: Array<{ id: number; path: string }>
    created_at: string
    updated_at: string
    count?: IntentSegmentCount
}

export interface IntentExportRun {
    id: string
    status: 'queued' | 'running' | 'completed' | 'failed'
    trigger: 'manual' | 'schedule'
    counts: { sent?: number; invalid?: number; pages?: number; note?: string }
    report: Record<string, unknown> | null
    error: string | null
    created_at: string
    completed_at: string | null
    download_available: boolean
}

export interface IntentExport {
    id: string
    name: string
    sub_tenant: string
    segment_id: string
    connector: 'csv' | 'meta' | 'google_ads' | 'klaviyo'
    /** Eligibility note for this connector (Google Customer Match), when there is one. */
    note?: string
    credential: { set: boolean; hint: string | null }
    explicit_only: boolean
    schedule: 'manual' | 'daily'
    active: boolean
    next_run_at: string | null
    last_run: IntentExportRun | null
}

const p = (projectId: string) => `/projects/${encodeURIComponent(projectId)}/intent`

export const intentApi = v2Api
    .enhanceEndpoints({ addTagTypes: ['IntentSegments', 'IntentExports'] })
    .injectEndpoints({
        endpoints: builder => ({
            getIntentStatus: builder.query<IntentStatus, string>({
                query: projectId => p(projectId),
                transformResponse: (res: V2Envelope<IntentStatus>) => unwrap(res),
            }),
            listIntentSegments: builder.query<IntentSegment[], string>({
                query: projectId => `${p(projectId)}/segments?counts=1`,
                transformResponse: (res: V2Envelope<IntentSegment[]>) => unwrap(res),
                providesTags: ['IntentSegments'],
            }),
            listIntentExports: builder.query<IntentExport[], string>({
                query: projectId => `${p(projectId)}/exports`,
                transformResponse: (res: V2Envelope<IntentExport[]>) => unwrap(res),
                providesTags: ['IntentExports'],
            }),
            runIntentExport: builder.mutation<IntentExportRun, { projectId: string; id: string }>({
                query: a => ({
                    url: `${p(a.projectId)}/exports/${encodeURIComponent(a.id)}/run`,
                    method: 'POST',
                }),
                transformResponse: (res: V2Envelope<IntentExportRun>) => unwrap(res),
                invalidatesTags: ['IntentExports'],
            }),
        }),
    })

export const {
    useGetIntentStatusQuery,
    useListIntentSegmentsQuery,
    useListIntentExportsQuery,
    useRunIntentExportMutation,
} = intentApi
